import {
  IFaxCoversheet,
  IFaxMessage,
  CoversheetField,
  CoversheetAutoFieldType,
  CoversheetCustomFieldType,
  CoversheetFieldSource,
  IFaxCoversheetContent,
  CoversheetMode,
  FaxCoversheetModeDb,
  FaxCoversheetMeta,
  FaxPreviewContent,
  IFaxDraftComposeState,
  RecipientDestination,
} from '@dataTypes/fax.type'
import { IContact } from '@dataTypes/contact.type'
import { IClinicNumber } from '@dataTypes/clinic.type'
import { FaxShowImages } from '@services/api/fax/settings.api'
import moment from 'moment'

export const COVERSHEET_AUTO_FIELD_TYPES: CoversheetAutoFieldType[] = [
  'recipient_name',
  'recipient_fax',
  'recipient_email',
  'sender_name',
  'sender_fax',
  'sender_email',
  'current_date',
]

export const isCoversheetAutoFieldType = (
  type: CoversheetField['type'],
): type is CoversheetAutoFieldType =>
  (COVERSHEET_AUTO_FIELD_TYPES as string[]).includes(type)

const newId = (): string =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`

export const htmlToPlainText = (html: string): string => {
  if (!html?.trim()) return ''
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
  const stripped = withBreaks.replace(/<[^>]+>/g, '')
  const decoded = stripped
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
  return decoded.replace(/\n{3,}/g, '\n\n').trim()
}

export const coversheetModeToDb = (
  mode: CoversheetMode,
): FaxCoversheetModeDb => {
  if (mode === 'template') return 'template'
  if (mode === 'uploaded') return 'uploaded'
  return 'skipped'
}

export const dbCoversheetModeToCompose = (
  mode?: FaxCoversheetModeDb | null,
): CoversheetMode => {
  if (mode === 'template') return 'template'
  if (mode === 'uploaded') return 'uploaded'
  return 'skipped'
}

export const parseDraftComposeState = (
  raw: string | null | undefined,
): IFaxDraftComposeState | null => {
  if (!raw?.trim()) return null
  try {
    const parsed = JSON.parse(raw) as IFaxDraftComposeState
    if (parsed?.version === 1 && typeof parsed.step === 'number') {
      return parsed
    }
  } catch {
    return null
  }
  return null
}

export const stringifyDraftComposeState = (
  state: IFaxDraftComposeState,
): string => JSON.stringify(state)

export const parseCoversheetContent = (
  raw: string | null | undefined,
): IFaxCoversheetContent | null => {
  if (!raw?.trim()) return null
  try {
    const parsed = JSON.parse(raw) as IFaxCoversheetContent
    if (parsed?.version === 1 && Array.isArray(parsed.fields)) {
      return {
        ...parsed,
        messageText:
          parsed.messageText ??
          (parsed.messageHtml ? htmlToPlainText(parsed.messageHtml) : ''),
      }
    }
  } catch {
    return null
  }
  return null
}

export const stringifyCoversheetContent = (
  content: IFaxCoversheetContent,
): string => JSON.stringify(content)

export const createEmptyCoversheetContent = (
  title = 'New cover sheet',
): IFaxCoversheetContent => ({
  version: 1,
  title,
  to: { optionalNote: '' },
  from: { displayName: '', faxNumber: '' },
  sentOn: moment().format('MMMM D, YYYY - h:mm A'),
  fields: [],
  messageHtml: '',
  messageText: '',
})

export const resolveAutoFieldValue = (
  type: CoversheetField['type'],
  ctx: {
    recipient?: IContact | null
    clinicLine?: IClinicNumber | null
    clinicName?: string
  },
): string => {
  const r = ctx.recipient
  switch (type) {
    case 'recipient_name':
      return r
        ? [r.first_name, r.last_name].filter(Boolean).join(' ').trim()
        : ''
    case 'recipient_fax':
      return r?.fax_number || ''
    case 'recipient_email':
      return r?.email || ''
    case 'sender_name':
      return (
        ctx.clinicLine?.line_name ||
        ctx.clinicName ||
        ctx.clinicLine?.phone_number ||
        ''
      )
    case 'sender_fax':
      return ctx.clinicLine?.phone_number || ''
    case 'sender_email':
      return ''
    case 'current_date':
      return moment().format('MMMM D, YYYY')
    default:
      return ''
  }
}

/** Resolve the dialable destination number for a recipient + chosen field. */
export const resolveRecipientToNumber = (
  recipient?: IContact | null,
): string => {
  if (!recipient) return ''

  // fax_number and email both still transmit via the dialable fax/cell number.
  return recipient.fax_number || ''
}

/** Write recipient details into the reserved `to` slot based on the chosen field. */
export const syncRecipientToContent = (
  content: IFaxCoversheetContent,
  recipient: IContact | null | undefined,
  destination: RecipientDestination,
): IFaxCoversheetContent => {
  if (!recipient) return content
  const name = [recipient.first_name, recipient.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
  const recipientFax =
    destination === 'fax_number' ? recipient.fax_number : recipient.email
  return {
    ...content,
    to: {
      ...content.to,
      contactId: recipient.contact_id,
      recipientName: name || content.to?.recipientName,
      recipientFax,
      recipientEmail: recipient.email || content.to?.recipientEmail,
    },
  }
}

/** Drop any legacy auto-type rows from `fields` before persisting. */
export const sanitizeCoversheetContent = (
  content: IFaxCoversheetContent,
): IFaxCoversheetContent => ({
  ...content,
  fields: content.fields.filter((f) => !isCoversheetAutoFieldType(f.type)),
})

// Max JSON body size for fax draft updates (under the server 10mb limit).
export const FAX_DRAFT_BODY_LIMIT_BYTES = 9 * 1024 * 1024

const formatDraftPayloadBytes = (bytes: number): string => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
  return `${Math.round(bytes / 1024)} KB`
}

// Estimate the PUT body size for a fax draft update.
export const estimateFaxDraftUpdateBodyBytes = (
  data: Record<string, unknown>,
): number =>
  new Blob([
    JSON.stringify({
      fax_id: '00000000-0000-0000-0000-000000000000',
      data,
    }),
  ]).size

// Returns a user-facing error when the draft payload exceeds the limit.
export const getFaxDraftPayloadSizeError = (
  data: Record<string, unknown>,
): string | null => {
  const bytes = estimateFaxDraftUpdateBodyBytes(data)
  if (bytes <= FAX_DRAFT_BODY_LIMIT_BYTES) return null
  return `The cover sheet content is too large (${formatDraftPayloadBytes(bytes)}). Remove pasted images or attach them as documents instead.`
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const buildCoversheetFieldLines = (
  content: IFaxCoversheetContent,
  format: 'html' | 'text',
): string[] => {
  const lines: string[] = []
  const toName =
    content.to?.recipientName || (content.to?.contactId ? 'Recipient' : '')
  const toFax = content.to?.recipientFax || ''

  if (toName || toFax) {
    const toValue = `${toName ?? ''} (${toFax ?? ''})`
    lines.push(
      format === 'html'
        ? `<div class="fax-cover-row"><strong>To:</strong> ${escapeHtml(toValue)}</div>`
        : `To: ${toValue}`,
    )
  }
  if (content.to?.recipientEmail) {
    lines.push(
      format === 'html'
        ? `<div class="fax-cover-row"><strong>Email:</strong> ${escapeHtml(content.to.recipientEmail)}</div>`
        : `Email: ${content.to.recipientEmail}`,
    )
  }
  if (content.to?.optionalNote) {
    lines.push(
      format === 'html'
        ? `<div class="fax-cover-row">${escapeHtml(content.to.optionalNote)}</div>`
        : content.to.optionalNote,
    )
  }
  if (content.from?.displayName || content.from?.faxNumber) {
    const fromValue = `${content.from.displayName ?? ''} (${content.from.faxNumber ?? ''})`
    lines.push(
      format === 'html'
        ? `<div class="fax-cover-row"><strong>From:</strong> ${escapeHtml(fromValue)}</div>`
        : `From: ${fromValue}`,
    )
  }
  if (content.title?.trim()) {
    lines.push(
      format === 'html'
        ? `<div class="fax-cover-row"><strong>Subject:</strong> ${escapeHtml(content.title)}</div>`
        : `Subject: ${content.title}`,
    )
  }
  if (content.sentOn) {
    lines.push(
      format === 'html'
        ? `<div class="fax-cover-row"><strong>Sent on:</strong> ${escapeHtml(content.sentOn)}</div>`
        : `Sent on: ${content.sentOn}`,
    )
  }

  for (const field of content.fields) {
    // Auto fields live in the reserved header slots; never render them as rows.
    if (isCoversheetAutoFieldType(field.type)) continue
    if (field.type === 'checklist') {
      const label = field.label || 'Checklist'
      if (format === 'html') {
        lines.push(
          `<div class="fax-cover-row"><strong>${escapeHtml(label)}:</strong></div><ul class="fax-cover-checklist">`,
        )
        for (const item of field.items ?? []) {
          const mark = item.checked ? '[x]' : '[ ]'
          lines.push(`<li>${mark} ${escapeHtml(item.label || '')}</li>`)
        }
        lines.push('</ul>')
      } else {
        lines.push(`${label}:`)
        for (const item of field.items ?? []) {
          const mark = item.checked ? '[x]' : '[ ]'
          lines.push(`  ${mark} ${item.label}`)
        }
      }
    } else if (field.label || field.value) {
      lines.push(
        format === 'html'
          ? `<div class="fax-cover-row"><strong>${escapeHtml(field.label || '')}:</strong> ${escapeHtml(field.value || '')}</div>`
          : `${field.label || ''}: ${field.value || ''}`,
      )
    }
  }

  return lines
}

export const renderCoversheetToHtml = (
  content: IFaxCoversheetContent,
  logoUrl?: string | null,
): string => {
  const logoRow = logoUrl
    ? `<div class="fax-cover-logo-row"><img class="fax-cover-logo" src="${escapeHtml(
        logoUrl,
      )}" alt="Clinic logo" /></div>`
    : '<div class="fax-cover-logo-row"></div>'
  const lines: string[] = [
    '<div class="fax-cover-document">',
    '<div class="fax-cover-header">',
    logoRow,
    '</div>',
    '<div class="fax-cover-body">',
  ]

  lines.push(...buildCoversheetFieldLines(content, 'html'))

  if (content.messageHtml?.trim()) {
    lines.push(
      `<div class="fax-cover-message"><strong>Message:</strong>${content.messageHtml}</div>`,
    )
  }

  lines.push('</div>', '</div>')
  return lines.join('\n')
}

export const renderCoversheetToPlainText = (
  content: IFaxCoversheetContent,
): string => {
  const lines: string[] = []
  lines.push(...buildCoversheetFieldLines(content, 'text'))
  const message =
    content.messageText?.trim() ||
    (content.messageHtml ? htmlToPlainText(content.messageHtml) : '')
  if (message) {
    lines.push('', 'MESSAGE:', message)
  }
  return lines.join('\n').trim()
}

export const applyTemplateToContent = (
  template: IFaxCoversheet,
  ctx: {
    clinicLine?: IClinicNumber | null
    clinicName?: string
    recipient?: IContact | null
  },
): IFaxCoversheetContent => {
  const base =
    parseCoversheetContent(template.content) ??
    createEmptyCoversheetContent(template.file_name || 'Cover Sheet')
  const sentOn = moment().format('MMMM D, YYYY - h:mm A')
  return {
    ...base,
    title: base.title || template.file_name || '',
    sentOn,
    from: {
      ...base.from,
      displayName: ctx.clinicName || 'Clinic',
      faxNumber: ctx.clinicLine?.phone_number || '',
    },
    to: {
      ...base.to,
      contactId: ctx.recipient?.contact_id,
      recipientName: ctx.recipient
        ? `${ctx.recipient.first_name} ${ctx.recipient.last_name}`.trim()
        : base.to?.recipientName,
      recipientFax:
        ctx.recipient?.fax_number ||
        ctx.recipient?.cellphone ||
        base.to?.recipientFax,
      recipientEmail: ctx.recipient?.email || base.to?.recipientEmail,
    },
  }
}

/**
 * Resolve a structured coversheet summary for the detail preview meta grid.
 * Reads from the reserved header slots, never from per-field rows.
 */
export const getFaxCoversheetMeta = (fax: IFaxMessage): FaxCoversheetMeta => {
  const mode = fax.coversheet_mode
  const coversheetType: FaxCoversheetMeta['coversheetType'] =
    mode === 'template' ? 'Template' : mode === 'uploaded' ? 'Uploaded' : 'None'

  let content: IFaxCoversheetContent | null =
    parseDraftComposeState(fax.draft_compose_state)?.coverContent ?? null
  if (!content && fax.coversheet?.content) {
    content = parseCoversheetContent(fax.coversheet.content)
  }

  const customFields = content
    ? content.fields
        .filter(
          (f) => !isCoversheetAutoFieldType(f.type) && f.type !== 'checklist',
        )
        .filter((f) => f.label || f.value)
        .map((f) => ({ label: f.label || '', value: f.value || '' }))
    : []

  return {
    toLabel: content?.to?.recipientName || '',
    toFax: content?.to?.recipientFax || fax.to_number || '',
    toEmail: content?.to?.recipientEmail,
    fromLabel: content?.from?.displayName || '',
    fromFax: content?.from?.faxNumber || fax.from_number || '',
    subject: content?.title || fax.subject || '',
    sentOn: content?.sentOn || '',
    coversheetType,
    templateName: fax.coversheet?.file_name ?? undefined,
    pageCount: fax.page_count ?? (fax.documents?.length || 1),
    customFields,
  }
}

export const createCoversheetField = (
  base: CoversheetCustomFieldType,
  source: CoversheetFieldSource,
  ctx: Parameters<typeof resolveAutoFieldValue>[1],
): CoversheetField => {
  const id = newId()
  if (base === 'checklist') {
    return {
      id,
      type: 'checklist',
      label: undefined,
      items: [
        { id: newId(), label: undefined, checked: false },
        { id: newId(), label: undefined, checked: false },
      ],
    }
  }
  if (source === 'custom') {
    return {
      id,
      type: base,
      label: undefined,
      value: undefined,
    }
  }
  const value = resolveAutoFieldValue(source, ctx)
  return {
    id,
    type: base,
    label: undefined,
    value,
    dateValue:
      base === 'date' && value
        ? moment(value, 'MMMM D, YYYY').toDate()
        : undefined,
  }
}

export const getComposePreviewContent = (
  coversheetMode: CoversheetMode,
  coverContent: IFaxCoversheetContent,
  logoUrl?: string | null,
): FaxPreviewContent => {
  if (coversheetMode === 'skipped') {
    return { kind: 'no_coversheet' }
  }
  if (coversheetMode === 'uploaded') {
    return { kind: 'not_available' }
  }
  return {
    kind: 'html',
    html: renderCoversheetToHtml(coverContent, logoUrl),
  }
}

export const getFaxPreviewContent = (fax: IFaxMessage): FaxPreviewContent => {
  const mode = fax.coversheet_mode
  if (mode === 'skipped' || mode === null || mode === undefined) {
    if (fax.cover_snapshot_html?.trim()) {
      return { kind: 'html', html: fax.cover_snapshot_html }
    }
    return { kind: 'no_coversheet' }
  }
  if (mode === 'uploaded') {
    return { kind: 'not_available' }
  }
  if (mode === 'template') {
    if (fax.cover_snapshot_html?.trim()) {
      return { kind: 'html', html: fax.cover_snapshot_html }
    }
    const fromCoversheet = fax.coversheet?.content
      ? parseCoversheetContent(fax.coversheet.content)
      : null
    if (fromCoversheet) {
      return {
        kind: 'html',
        html: renderCoversheetToHtml(fromCoversheet),
      }
    }
    const fromDraft = parseDraftComposeState(fax.draft_compose_state)
    if (fromDraft?.coverContent) {
      return {
        kind: 'html',
        html: renderCoversheetToHtml(fromDraft.coverContent),
      }
    }
  }
  return { kind: 'no_coversheet' }
}

export const getFaxPreviewHtml = (fax: IFaxMessage): string => {
  const preview = getFaxPreviewContent(fax)
  if (preview.kind === 'html' && preview.html) {
    return preview.html
  }
  if (preview.kind === 'not_available') {
    return '<p class="fax-cover-empty">Preview not available</p>'
  }
  return '<p class="fax-cover-empty">This fax has no coversheet</p>'
}

export const stripCoversheetImages = (
  html: string,
  clickable = false,
): string =>
  html.replace(
    /<img\b[^>]*>/gi,
    clickable
      ? '<span class="fax-cover-image-hidden fax-cover-image-hidden--clickable" role="button" tabindex="0" data-fax-image-placeholder="true"><i class="mgc_pic_line"></i> Image hidden — click to show</span>'
      : '<span class="fax-cover-image-hidden">[image hidden]</span>',
  )

export const coversheetHasImages = (html: string): boolean =>
  /<img\b/i.test(html)

export const applyShowImagesToHtml = (
  html: string,
  showImages?: FaxShowImages,
  revealed = false,
): string => {
  if (showImages === 'never') return stripCoversheetImages(html, false)
  if (showImages === 'ask')
    return revealed ? html : stripCoversheetImages(html, true)
  return html
}

export const previewContentToHtml = (preview: FaxPreviewContent): string => {
  if (preview.kind === 'html' && preview.html) {
    return preview.html
  }
  if (preview.kind === 'not_available') {
    return '<p class="fax-detail-attachments-empty">No contents to show</p>'
  }
  return '<p class="fax-detail-attachments-empty">No contents to show</p>'
}
